// Резолвинг template-строки iframe-поля в финальный URL.
//
// Принимает либо абсолютный http(s)-URL, либо чистый `<iframe ...>...</iframe>`
// embed-код, либо template c переменными `{{userId}}`/`{{userEmail}}`/
// `{{recordId}}`/`{{catalogId}}`.
//
// Возвращает:
//   * `{ url, width, height, isSameOrigin }` — успешно разрезолвлено
//   * `UNRESOLVED_MARKER` — в template остались плейсхолдеры без значения
//   * `null` — пусто / невалидный URL / опасная схема

// non-global — для test() (избегаем lastIndex между вызовами)
const PLACEHOLDER_REGEXP_TEST = /\{\{[^}]+\}\}/;
// global — для replace() (обрабатывает все совпадения за один проход)
const PLACEHOLDER_REGEXP_REPLACE = /\{\{([^}]+)\}\}/g;

// Строгий парсер embed: вся строка должна быть только `<iframe ...>...</iframe>`.
// Обёртки вроде `<div><iframe>` не распознаются — пользователь подаёт либо
// чистый embed, либо plain URL.
const IFRAME_TAG_REGEXP = /^<iframe\b([^>]*)>[\s\S]*?<\/iframe>$/i;

const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

export const UNRESOLVED_MARKER = '__IFRAME_UNRESOLVED__';

// Поддерживаемые переменные. `{{fieldId}}` намеренно убран — для подстановки
// значений других полей нужно использовать формулу.
const CONTEXT_VARIABLES = {
  userId: (ctx) => ctx.user && String(ctx.user.get('id') || ''),
  userEmail: (ctx) => ctx.user && String(ctx.user.get('email') || ''),
  recordId: (ctx) => (ctx.recordId != null ? String(ctx.recordId) : ''),
  catalogId: (ctx) => (ctx.catalogId != null ? String(ctx.catalogId) : ''),
};

function safeEncode(value) {
  try {
    return encodeURIComponent(value);
  } catch (e) {
    return '';
  }
}

// Декодирует HTML-сущности через <textarea>-trick: используем нативный парсер
// браузера. Безопасно — textarea не выполняет скрипты, читаем только `.value`.
function decodeHtmlEntities(s) {
  if (typeof document === 'undefined') return s;
  const el = document.createElement('textarea');
  el.innerHTML = s;
  return el.value;
}

// Достаёт значение атрибута из строки. Поддерживает три формата:
// `name="value"`, `name='value'`, `name=value` (без кавычек, до whitespace/`>`).
function extractAttr(attrs, name) {
  const re = new RegExp(
    `\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
    'i',
  );
  const m = re.exec(attrs);
  if (!m) return null;
  return m[1] != null ? m[1] : m[2] != null ? m[2] : m[3] != null ? m[3] : null;
}

// `"600"` → `"600px"` (число без единиц), `"100%"` → `"100%"` (с единицей as-is).
function normalizeDimension(value) {
  if (value == null) return null;
  const v = String(value).trim();
  if (!v) return null;
  return /^\d+$/.test(v) ? `${v}px` : v;
}

function substituteVariables(template, ctx) {
  return template.replace(PLACEHOLDER_REGEXP_REPLACE, (match, key) => {
    const trimmedKey = key.trim();
    if (trimmedKey in CONTEXT_VARIABLES) {
      const v = CONTEXT_VARIABLES[trimmedKey](ctx);
      return v == null ? '' : safeEncode(v);
    }
    // Не наша переменная — оставляем как есть, чтобы засечь как unresolved.
    return match;
  });
}

function parseEmbed(result) {
  const tagMatch = IFRAME_TAG_REGEXP.exec(result.trim());
  if (!tagMatch) return null;
  const attrs = tagMatch[1];
  return {
    url: extractAttr(attrs, 'src') || result,
    width: normalizeDimension(extractAttr(attrs, 'width')),
    height: normalizeDimension(extractAttr(attrs, 'height')),
  };
}

/**
 * @param {string} template — шаблон URL (либо абсолютный URL, либо embed-код, либо с {{...}})
 * @param {object} context — { user, recordId, catalogId }
 *   user — Immutable Map текущего пользователя (`['user']` из стора)
 *   recordId — id текущей записи (из params в ControlList)
 *   catalogId — id текущего каталога (из params в ControlList)
 * @returns {{url, width, height, isSameOrigin} | typeof UNRESOLVED_MARKER | null}
 */
export function resolveIframeUrl(template, context) {
  if (!template || typeof template !== 'string' || !template.trim()) {
    return null;
  }

  const ctx = context || {};
  const substituted = substituteVariables(template, ctx);

  // Остались нерезолвленные `{{...}}` — сигналим спец-маркером, чтобы UI
  // показал плейсхолдер «Содержит незаполненные переменные».
  if (PLACEHOLDER_REGEXP_TEST.test(substituted)) {
    return UNRESOLVED_MARKER;
  }

  // Если это embed-код — извлекаем src + width + height. Атрибуты пользователя
  // (sandbox, onload, srcdoc, allow, и т.п.) намеренно выкидываются — iframe
  // пересобирается с нашими атрибутами в компоненте.
  const embed = parseEmbed(substituted);
  const rawUrl = embed ? embed.url : substituted;
  const width = embed ? embed.width : null;
  const height = embed ? embed.height : null;

  // Декодируем HTML-сущности (&amp; → &). Embed-коды от YouTube/Maps/etc.
  // приходят с entity-encoded ampersand'ами; без декодирования query-параметры
  // ломаются на стороне сервиса.
  const url = decodeHtmlEntities(rawUrl).trim();

  if (!url) {
    return null;
  }

  // Парсим как абсолютный URL. Без base-аргумента относительные URL и любая
  // не-абсолютная строка (`{Текст}`, `мусор`, `/path`) выбросят TypeError и
  // отвергнутся. Ожидаем только абсолютные http(s)-URL.
  let parsed;
  try {
    parsed = new URL(url);
  } catch (e) {
    return null;
  }

  if (!SAFE_PROTOCOLS.has(parsed.protocol)) {
    return null;
  }

  const isSameOrigin = typeof window !== 'undefined'
    && window.location
    && parsed.origin === window.location.origin;

  return { url: parsed.toString(), width, height, isSameOrigin };
}
